// InlineAccordion — a disclosure widget shaped like react-bootstrap's
// <Accordion> (header + body) but inline-styled: the trigger is a small
// inline-flex button with a rotating chevron rather than a full-width
// bar, so it can sit in chat bubbles, list items, or other prose flows
// without dominating the layout. The body still drops below as a block
// when expanded.
//
// Supports controlled (`open` + `onToggle`) and uncontrolled
// (`defaultOpen`) modes. Use the controlled form when something else —
// e.g. a "collapse when streaming finishes" effect — owns the state.
import React, { useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';

export interface InlineAccordionProps {
    header: React.ReactNode;
    children: React.ReactNode;
    /** Controlled open state. Pair with `onToggle`. */
    open?: boolean;
    /** Initial open state in uncontrolled mode. Default false. */
    defaultOpen?: boolean;
    /** Fires with the next open state when the trigger is clicked. */
    onToggle?: (next: boolean) => void;
    /** Optional className applied to the outer wrapper. */
    className?: string;
    /** Optional style applied to the body container. */
    bodyStyle?: React.CSSProperties;
}

export const InlineAccordion = ({
    header,
    children,
    open: openProp,
    defaultOpen = false,
    onToggle,
    className,
    bodyStyle,
}: InlineAccordionProps) => {
    const [openState, setOpenState] = useState(defaultOpen);
    const controlled = openProp !== undefined;
    const open = controlled ? openProp : openState;
    const toggle = () => {
        const next = !open;
        if (!controlled) setOpenState(next);
        onToggle?.(next);
    };
    return (
        <div className={className}>
            <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                className="btn btn-link p-0 text-muted text-decoration-none d-inline-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem' }}
            >
                <BsChevronDown
                    size="0.8em"
                    style={{
                        transition: 'transform 0.15s ease',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                />
                <span>{header}</span>
            </button>
            {open && (
                <div
                    className="mt-1 mb-0 p-2 rounded"
                    style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px dashed rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: '0.78rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '24rem',
                        overflowY: 'auto',
                        ...bodyStyle,
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default InlineAccordion;
